// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import {api} from './index'
import {JobType, JobStatus} from 'constants/job'

export interface IJob {
  jobID: string
  jobName: string
  deviceID: string,
  uploadedImageCount: number
  jobType: JobType
  daily: boolean
  // if daily is true, date should be left NULL
  startDate: string
  endDate: string
  startTime: string
  endTime: string
  createdAt: string
  status: JobStatus
  successTaskCount: number
  taskTotalCount: number
  creatorPlatform: number
  groupID: number
  creatorName: string
  groupJobID: string
  photoDirection: number
  orgCode: string
}

export interface IJobsQueryParams {
  pageIndex: number
  pageSize: number
  cityID: number
  filters: {
    jobType?: JobType;
    status?: JobStatus;
    photoDirection?: number[];
    groupJobID?: string[];
  }
}

export interface IJobsResponse {
  jobs: IJob[]
  current: number
  total: number
  pageSize: number
}
export function getDownloadJobs(params: IJobsQueryParams): Promise<IJobsResponse> {
  return api.post('/getdownloadjobs', params)
}

export interface ICreateJobParams {
  deviceID?: string,
  jobName: string
  startDate: string
  endDate: string
  startTime: string
  endTime: string
  jobType: JobType
  points?: {lon: number; lat: number}[]
  cityID: number
  roadID?: number
  groupID?: number
}
export const createDownloadJob = async ({
  jobName,
  deviceID,
  roadID = null,
  startDate,
  endDate,
  startTime,
  endTime,
  jobType,
  points = [],
  cityID,
  groupID
}: ICreateJobParams) => {
  if ((!deviceID) && roadID === null && points.length === 0) {
    console.log('invalid param')
    return
  }
  return api.post('/downloadjob', {
    jobName,
    startDate,
    endDate,
    startTime,
    endTime,
    roadID,
    deviceID,
    jobType,
    points,
    cityID,
    groupID
  })
}

export function deleteJob(jobId: string) {
  return api.delete('/downloadjob', {data: {jobID: jobId}})
}

export interface IUpdateJobParams {
  jobID: string
  // hh:mm:ss
  startTime: string
  endTime: string
  startDate: string
  endDate: string
}
export function modifyJob(params: IUpdateJobParams) {
  return api.put('/downloadjob', params)
}

export type IDeviceGeoItem = {
  deviceID: string
  latitude: number
  longitude: number
}
export type IRoadGeo = number[][]
export type IGeofenceGeo = number[][]
export type IJobGeoResponse = IDeviceGeoItem[] | IRoadGeo | IGeofenceGeo
export function getJobGeo(jobId: string): Promise<IJobGeoResponse> {
  return api.get(`downloadjob/${jobId}/geo`)
}

export function pauseJob(jobID: string) {
  return api.put(`/downloadjob/${jobID}/pause`)
}

export function resumeJob(jobID: string) {
  return api.put(`/downloadjob/${jobID}/resume`)
}
export interface ITasksQueryParams {
  pageIndex: number
  pageSize: number
  startTime: string
  endTime: string
  jobID: string
}
export interface ITask {
  startTime: string
  endTime: string
  status: number
  deviceID: string
  uploadedImageCount: number
  sequenceID: number
  createdAt: string
  taskID: string
  environment: number
  blobImageCount: number
  lastUpdateAt: number
}

export interface ITasksResponse {
  jobs: ITask[]
  current: number
  total: number
  pageSize: number
}
export function getTasks(params: ITasksQueryParams): Promise<ITasksResponse> {
  const {jobID, ...restParams} = params
  return api.get(`/job/${jobID}/tasks`, {params: restParams})
}
export function stopJob(jobID: string) {
  return api.put(`/job/${jobID}/stop`)
}
export function stopTask(jobID: string, taskID: string) {
  return api.put(`/job/${jobID}/task/${taskID}/stop`)
}

export interface IDetailItem {
  code: number
  name: string
  count: number
}
export interface ITaskResponse {
  results: {
    taskID: number
    deviceID: string
    status: number
    stitchSuccessCount: number
    stitchFailCount: number
    stitchDetails: IDetailItem[]
  }
  metadata: object
}

export function fetchTaskInfo(taskId: string): Promise<ITaskResponse> {
  return api.get(`/v1/task/${taskId}`)
}
